/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { findBy, mapBy } from '@linc-technologies/linced-common/utils/arrays';

export default class BestFitSummary extends Component {
  // Props
  // categories provides all school goal categories to generate goal progress
  // reports for.
  categories = null;

  // person provides the person to generate goal progress reports for.
  person = null;

  levels = null;
  summary = null;

  // algorithmVersion defaults to 2 (latest).
  @tracked algorithmVersion = 2;

  // data computes the data required to display the best fit summary.
  @computed(
    'summary.categories.[]',
    'levels.[]',
    'categories.[]',
    'algorithmVersion',
  )
  get data() {
    let levelSummaryData = [];
    let bestFit = {
      firstMajorityMarkedLevel: null,
      majorityMarkedLevel: null,
      inProgressLevel: null,
      lastCompleteLevel: null,
      level: null,
      explanation: null,
      newAlgorithm: null,
      newAlgorithmExplanation: null,
      highestComplete: null,
    };
    let categories = this.get('summary.categories');
    let levels = this.levels;

    if (isEmpty(categories)) {
      return levelSummaryData;
    }

    let categoryIds = mapBy(categories, 'categoryId');
    let goalCategories = this.categories
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      )
      .filter((item) => categoryIds.includes(item.id));

    goalCategories.forEach(function (goalCategory) {
      let categoryStates = findBy(categories, 'categoryId', goalCategory.id);
      if (isEmpty(categoryStates)) {
        return;
      }

      let categoryLevels = levels
        .filter((level) => level.goalCategoryId?.id === goalCategory.id)
        .sort(
          (a, b) =>
            a.sequence - b.sequence ||
            (a.label || '').localeCompare(b.label || ''),
        );
      let numLevels = categoryLevels.length;
      let categoryTotal = categoryStates.totalGoals;
      let categorySecure = categoryStates.secure;
      let categoryDeveloping = categoryStates.developing;
      let categorySummary = {
        id: goalCategory.id,
        label: goalCategory.label,
        secure: categoryStates.secure,
        developing: categoryStates.developing,
        notSet: categoryStates.notSet,
        totalGoals: categoryStates.totalGoals,
        isBestFit: false,
      };

      let goalsPerLevel = Math.floor(categoryStates.totalGoals / numLevels);
      let percentOfCategory = 0;
      let lastPercentOfCategory = 0;
      let lastLevelinCat = false;

      categoryLevels.forEach((level, pos) => {
        let numGoalsSet = categorySecure + categoryDeveloping;

        let levelSummary = {
          id: level.id,
          level,
          categoryId: goalCategory.id,
          category: goalCategory,
          categorySummary: null,
          totalGoals: categoryTotal,
          secure: 0,
          developing: 0,
          notSet: 0,
        };

        if (pos === 0) {
          // Add category summary to the first level for each category.
          levelSummary.categorySummary = categorySummary;
        }

        if (goalsPerLevel < 1) {
          // No goals at this level.  Skip.
          levelSummaryData.push(levelSummary);
          return;
        }

        if (categoryStates.secure === categoryStates.totalGoals) {
          // This level is complete!
          bestFit.lastCompleteLevel = level;
          bestFit.majorityMarkedLevel = null;
          bestFit.inProgressLevel = null;
          let offset = Math.floor(goalsPerLevel * pos);
          if (pos === categoryLevels.length - 1) {
            // Last level for this category - round to total
            lastLevelinCat = true;
            goalsPerLevel = categoryStates.totalGoals - offset;
            levelSummary.goalsPerLevel = categoryStates.totalGoals - offset;
          }
          levelSummary.secure = goalsPerLevel;
          levelSummary.developing = 0;
        } else if (numLevels === 1) {
          // This is the only Level associated with this Category
          if (numGoalsSet > 0) {
            if (numGoalsSet >= Math.floor(categoryStates.totalGoals / 2)) {
              // The majority of this level is either complete or in
              // progress.
              if (isEmpty(bestFit.majorityMarkedLevel)) {
                // We don't yet have a majority-marked level higher
                // than the highest complete level; remember this one.
                bestFit.majorityMarkedLevel = level;
              }
              if (isEmpty(bestFit.firstMajorityMarkedLevel)) {
                // We don't yet have a majority-marked level; remember this one.
                bestFit.firstMajorityMarkedLevel = level;
              }
            } else {
              // This level is in progress, but a minority of goals are
              // complete / in progress.
              if (isEmpty(bestFit.inProgressLevel)) {
                // We don't yet have an in-progress level higher than
                // the highest complete level; remember this one.
                bestFit.inProgressLevel = level;
              }
            }
          }

          levelSummary.secure = categorySecure;
          levelSummary.developing = categoryDeveloping;
        } else {
          // There are other Levels associated with this Category, so we'll
          // have to split the Goals between them.
          let offset = Math.floor(goalsPerLevel * pos);
          if (pos === categoryLevels.length - 1) {
            // Last level for this category - round to total
            lastLevelinCat = true;
            goalsPerLevel = categoryStates.totalGoals - offset;
            levelSummary.goalsPerLevel = categoryStates.totalGoals - offset;
          }

          if (categoryStates.secure >= offset + goalsPerLevel) {
            // This level is complete!
            bestFit.lastCompleteLevel = level;
            bestFit.majorityMarkedLevel = null;
            bestFit.inProgressLevel = null;

            levelSummary.secure = goalsPerLevel;
            levelSummary.developing = 0;
          } else if (numGoalsSet > offset) {
            if (numGoalsSet >= offset + Math.floor(goalsPerLevel / 2)) {
              // The majority of this level is either complete or in
              // progress.
              if (isEmpty(bestFit.majorityMarkedLevel)) {
                // We don't yet have a majority-marked level higher
                // than the highest complete level; remember this one.
                bestFit.majorityMarkedLevel = level;
              }
              if (isEmpty(bestFit.firstMajorityMarkedLevel)) {
                // We don't yet have a majority-marked level; remember this one.
                bestFit.firstMajorityMarkedLevel = level;
              }
            } else {
              // This level is in progress, but a minority of goals are complete / in progress.
              // maxAvailableGoals = maxAvailableGoals - goalsPerLevel;
              if (isEmpty(bestFit.inProgressLevel)) {
                // We don't yet have an in-progress level higher than the highest complete level; remember this one.
                bestFit.inProgressLevel = level;
              }
            }

            let levelSecure = categorySecure - offset;
            levelSummary.secure = Math.max(0, levelSecure);
            let numNotSecure = goalsPerLevel - levelSummary.secure;
            let levelDeveloping = categoryDeveloping;
            if (levelSummary.secure === 0) {
              // No secure goals at this level.
              // All goals set above the offset are therefore "developing".
              levelDeveloping = numGoalsSet - offset;
            }
            levelSummary.developing = Math.min(numNotSecure, levelDeveloping);
          }
        }
        levelSummary.goalsPerLevel = goalsPerLevel;
        percentOfCategory = Math.floor((goalsPerLevel / categoryTotal) * 100);
        levelSummary.percentOfCategory =
          lastPercentOfCategory + percentOfCategory;
        if (lastLevelinCat === true) {
          levelSummary.percentOfCategory = 100;
        }
        levelSummary.lastPercentOfCategory = lastPercentOfCategory;
        lastPercentOfCategory += Math.floor(
          (goalsPerLevel / categoryTotal) * 100,
        );
        levelSummary.numLevels = numLevels;
        levelSummary.notSet =
          levelSummary.goalsPerLevel -
          levelSummary.secure -
          levelSummary.developing;
        levelSummary.percentSecure = Math.floor(
          (levelSummary.secure / levelSummary.goalsPerLevel) * 100,
        );
        levelSummary.percentDeveloping = Math.floor(
          (levelSummary.developing / levelSummary.goalsPerLevel) * 100,
        );
        levelSummaryData.push(levelSummary);
      });
    });

    // Determine best fit (algorithm v2)
    if (!isEmpty(bestFit.majorityMarkedLevel)) {
      // Learner has a majority-marked level after the last
      // fully-completed one.
      bestFit.explanation =
        ' is the first progress level after a completed progress level, that has more than 50% of the goals set.';
      bestFit.level = bestFit.majorityMarkedLevel;
    } else if (!isEmpty(bestFit.inProgressLevel)) {
      // Learner has started working on a Level after the last fully-completed one.
      // Learner has started working on a Level after the last
      // fully-completed one.
      bestFit.explanation =
        ' is the lowest progress level after a completed progress level that has any goals set.';
      bestFit.level = bestFit.inProgressLevel;
    } else if (!isEmpty(bestFit.lastCompleteLevel)) {
      // Learner has not started working on a Level after the last
      // fully-completed one.
      bestFit.explanation =
        ' is the last "complete" progress level and no goals are set at any higher progress levels.';
      bestFit.level = bestFit.lastCompleteLevel;
    }

    // If this was created under algorithm v1, adjust if needed.
    if (this.algorithmVersion === 1) {
      let hasFirstMajorityMarked = !isEmpty(bestFit.firstMajorityMarkedLevel);
      if (
        hasFirstMajorityMarked &&
        bestFit.level.id !== bestFit.firstMajorityMarkedLevel.id
      ) {
        bestFit.newAlgorithm = bestFit.level;
        bestFit.newAlgorithmExplanation = bestFit.explanation;
        bestFit.level = bestFit.firstMajorityMarkedLevel;
        bestFit.explanation =
          ' is the first progress level that has more than 50% of the goals set.';
      }
    }

    // Flag the appropriate category as containing the best fit.
    if (!isEmpty(bestFit.level)) {
      // Find the level summary for the best fit level.
      let bestFitSummary = findBy(levelSummaryData, 'id', bestFit.level.id);
      if (!isEmpty(bestFitSummary)) {
        // Find the first level summary with the same category ID.
        let firstSummaryForCategory = findBy(
          levelSummaryData,
          'categoryId',
          bestFitSummary.categoryId,
        );
        if (
          !isEmpty(firstSummaryForCategory) &&
          !isEmpty(firstSummaryForCategory.categorySummary)
        ) {
          // Set the best fit flag on the category summary.
          firstSummaryForCategory.categorySummary.isBestFit = true;
        }
      }
    }

    return {
      rows: levelSummaryData,
      bestFit,
    };
  }
}
